import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useApi } from '~/composables/useApi'
import { normalizeTheme } from '~/themes/types'

const DEFAULT_PRIMARY_COLOR = '#D0FE1D'

function normalizePrimaryColor(value) {
  const color = String(value ?? '').trim()

  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(color)) {
    if (color.length === 4) {
      return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
    }

    return color
  }

  return DEFAULT_PRIMARY_COLOR
}

function normalizePhone(value) {
  const digits = String(value ?? '').replace(/\D/g, '')

  if (!digits) {
    return ''
  }

  if (digits.startsWith('62')) {
    return digits
  }

  if (digits.startsWith('0')) {
    return `62${digits.slice(1)}`
  }

  return `62${digits}`
}

function readPopupLogin(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { title: '', items: [] }
  }

  const items = Array.isArray(value.items)
    ? value.items.map((item) => String(item ?? '').trim()).filter(Boolean)
    : []

  return {
    title: String(value.title ?? '').trim(),
    items,
  }
}

function readSocial(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { text: '', link: '' }
  }

  return {
    text: String(value.text ?? '').trim(),
    link: String(value.link ?? '').trim(),
  }
}

function readIcons(value) {
  if (!Array.isArray(value)) {
    return {}
  }

  /** @type {Record<string, string>} */
  const icons = {}

  for (const item of value) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      continue
    }

    const key = String(item.key ?? '').trim()
    const url = String(item.url ?? '').trim()

    if (key && url) {
      icons[key] = url
    }
  }

  return icons
}

const SOCIAL_CHANNELS = [
  ['whatsapp', 'WhatsApp'],
  ['telegram', 'Telegram'],
  ['instagram', 'Instagram'],
  ['tiktok', 'TikTok'],
  ['youtube', 'YouTube'],
]

export const useServerStore = defineStore('server', () => {
  const info = ref(null)
  const pending = ref(true)
  const error = ref(null)
  const initialized = ref(false)

  const logo = computed(() => info.value?.logo ?? '')
  const favicon = computed(() => info.value?.favicon ?? '')
  const name = computed(() => info.value?.name ?? '')
  const footer = computed(() => info.value?.footer?.trim() ?? '')
  const headline = computed(() => info.value?.headline ?? '')
  const theme = computed(() => normalizeTheme(info.value?.theme))
  const primaryColor = computed(() =>
    normalizePrimaryColor(info.value?.color?.primaryColor),
  )
  const googleTagManagerId = computed(
    () => info.value?.analytics?.google_tag_manager ?? '',
  )
  const hasSnk = computed(() => Boolean(info.value?.has_snk))
  const popupLogin = computed(() => readPopupLogin(info.value?.popup_login))
  const icons = computed(() => readIcons(info.value?.icons))
  const hasTheme = computed(() => String(info.value?.theme ?? '').trim() !== '')
  const unavailable = computed(
    () => initialized.value && !pending.value && Boolean(error.value),
  )

  const phone = computed(() => normalizePhone(info.value?.hp))

  const socialLinks = computed(() =>
    SOCIAL_CHANNELS.map(([key, label]) => ({
      key,
      label,
      ...readSocial(info.value?.[key]),
    })).filter((item) => item.link),
  )

  const contactLinks = computed(() =>
    socialLinks.value.filter(
      (item) => item.key === 'whatsapp' || item.key === 'telegram',
    ),
  )

  const followLinks = computed(() =>
    socialLinks.value.filter(
      (item) => item.key !== 'whatsapp' && item.key !== 'telegram',
    ),
  )

  let inflight = null

  async function fetchServerInfo() {
    if (inflight) {
      return inflight
    }

    const api = useApi()
    pending.value = true
    error.value = null

    inflight = (async () => {
      try {
        const response = await api('/api/v1/web-game/server-info')
        info.value = response?.data ?? null
      } catch (err) {
        error.value = err
        info.value = null
      } finally {
        pending.value = false
        initialized.value = true
        inflight = null
      }

      return info.value
    })()

    return inflight
  }

  async function ensureServerInfo() {
    if (!initialized.value) {
      await fetchServerInfo()
    }
    return info.value
  }

  return {
    info,
    pending,
    error,
    initialized,
    logo,
    favicon,
    name,
    footer,
    headline,
    theme,
    primaryColor,
    googleTagManagerId,
    hasSnk,
    popupLogin,
    icons,
    hasTheme,
    unavailable,
    phone,
    socialLinks,
    contactLinks,
    followLinks,
    fetchServerInfo,
    ensureServerInfo,
  }
})
